import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { all, takeEvery, select, put, call, takeLatest, delay } from 'redux-saga/effects';
import { moveNext, movePrev, setCurrentItem, setCurrentStreamAndItem, exitDuperbrowse, openDuperbrowse, shutdownDuperbrowse, exitDuperbrowseDone, loadDuperbrowseItem, loadDuperbrowse, openDuperbrowseWithEntities, unloadDuperbrowseItem, exitDuperbrowseResorationTimeout } from './actionCreators';
import { getNextItem, getPrevItem, getIsValidStreamItem, getCurrentStreamId, getCurrentItem, getRootLocation, getCurrentStreamSchema, getIsAnyStreamActive, getIsDuperbrowseActive, getIsHistoryManagementEnabled, getNeedsToLoadMore } from './selectors';
import { getStreamItemId, getStreamItemUrl, isDeviationStream, isDuperbrowseLocation, getNormalizedStreamItemId, getBiEventData } from './helpers';
import { duperbrowseHistoryChanged, historyChanged } from '../../redux/routing/actions';
import history from '../../redux/routing/history';
import { createLocation } from 'history';
import { isSimpleStream } from './types';
import { handleLoading } from './loader';
import ddt from '@wix/da-ddt';
import { normalize } from 'normalizr';
import { deviationListSchema } from '../../redux/normalizr/schemas/deviation';
import { logBiEvent } from '../../biLogger/redux/saga';
import { getPageBiContext } from '../../biLogger/redux/selectors';
import { actions as streamActions } from '../../Stream';
import { scrollTo } from '../../utils/browser';
import { isExtendedDeviationFullyLoaded } from '../../DeviationPage/redux/helpers';
const logger = ddt.createLogger('duperbrowse');
function getNonDuperHistoryState() {
    const _ref = history.location.state || {}, { duperbrowseState, duperbrowseRoot } = _ref, restState = _object_without_properties(_ref, [
        "duperbrowseState",
        "duperbrowseRoot"
    ]);
    return restState;
}
function* updateHistoryWith(item) {
    const isEnabled = yield select(getIsHistoryManagementEnabled);
    if (!isEnabled) {
        return;
    }
    const streamId = yield select(getCurrentStreamId);
    const url = getStreamItemUrl(item);
    const itemId = getStreamItemId(item);
    if (!isExtendedDeviationFullyLoaded(item)) {
        logger.log('Skipping URL update as extended not fully loaded', itemId);
    // return;
    }
    if (!streamId || !url || !itemId) {
        return;
    }
    const state = {
        duperbrowseState: {
            streamId,
            itemId
        }
    };
    const newLocation = createLocation(url, state, undefined, history.location);
    history.push(newLocation);
}
function* moveToItem(item, direction) {
    // TODO: deal with loading state
    if (!item) {
        return;
    }
    const biContext = yield select(getPageBiContext);
    const currentItem = yield select(getCurrentItem);
    const schema = yield select(getCurrentStreamSchema);
    const itemId = getNormalizedStreamItemId(item);
    logger.log(`Moving to ${direction} item`, item);
    if (currentItem) {
        yield call(logBiEvent, _object_spread({}, biContext, getBiEventData(currentItem, direction, item)));
    }
    yield put(unloadDuperbrowseItem(currentItem));
    yield call(updateHistoryWith, item);
    yield all([
        put(setCurrentItem(itemId)),
        put(loadDuperbrowseItem(itemId, schema, direction))
    ]);
    const needsToLoadMore = yield select(getNeedsToLoadMore, direction);
    if (needsToLoadMore) {
        const streamId = yield select(getCurrentStreamId);
        yield put(streamActions.fetch(streamId, direction));
    }
}
export function* handleMoveNext() {
    const nextItem = yield select(getNextItem);
    yield call(moveToItem, nextItem, 'next');
}
export function* handleMovePrev() {
    const prevItem = yield select(getPrevItem);
    yield call(moveToItem, prevItem, 'prev');
}
function* handleHistoryChange({ payload: { location, action } }) {
    // we only need to handle popstates
    if (action !== 'POP') {
        return;
    }
    // if there is no state, bail
    if (!location.state) {
        window.location.reload();
        return;
    }
    // if we arrived at our entry point, exit session wherever we were
    if (location.state.duperbrowseRoot === true) {
        const isDuperActive = yield select(getIsDuperbrowseActive);
        yield put(shutdownDuperbrowse());
        // clean up
        history.replace(_object_spread_props(_object_spread({}, history.location), {
            state: getNonDuperHistoryState()
        }));
        // if duperbrowse is not active and we landed on a non-duper url,
        // reload
        const isDuperLocation = isDuperbrowseLocation(location, true);
        if (!isDuperActive && !isDuperLocation) {
            window.location.reload();
            return;
        }
        yield put(exitDuperbrowseDone());
        return;
    }
    // if there is no non-root state, bail
    if (!location.state.duperbrowseState) {
        window.location.reload();
        return;
    }
    const { streamId, itemId } = location.state.duperbrowseState;
    // make sure this item actually exists in our streams
    const isValid = yield select(getIsValidStreamItem, streamId, itemId);
    if (!isValid) {
        window.location.reload();
    }
    const currentItem = yield select(getCurrentItem);
    yield all([
        put(unloadDuperbrowseItem(currentItem)),
        put(setCurrentStreamAndItem(streamId, itemId))
    ]);
}
function* handleNonDuperHistoryChange({ payload: { action } }) {
    // we only need to handle popstates
    if (action !== 'POP') {
        return;
    }
    // if duper is not active, nothing to do here
    const isDuperActive = yield select(getIsAnyStreamActive);
    if (!isDuperActive) {
        return;
    }
    // everything is terrible, just reload for sanity
    window.location.reload();
}
function* handleLoadDuperbrowse({ payload: { itemId, stream } }) {
    const currentItem = yield select(getCurrentItem);
    if (currentItem) {
        yield put(unloadDuperbrowseItem(currentItem));
    }
    if (isSimpleStream(stream) && isDeviationStream(stream)) {
        const { result, entities } = normalize(stream.items, deviationListSchema);
        yield put(openDuperbrowseWithEntities(itemId, _object_spread_props(_object_spread({}, stream), {
            items: result,
            itemSchema: 'deviation'
        }), entities));
        return;
    }
    yield put(openDuperbrowse(itemId, stream));
}
function* handleOpenDuperbrowse({ payload: { currentOpenItem, itemSchema } }) {
    const isHistoryManagementEnabled = yield select(getIsHistoryManagementEnabled);
    const currentItem = yield select(getCurrentItem);
    if (!currentItem) {
        return;
    }
    logger.log('Opening duperbrowse', currentItem);
    const isRoot = !(history.location.state && history.location.state.duperbrowseState);
    if (isRoot && isHistoryManagementEnabled) {
        history.replace(_object_spread_props(_object_spread({}, history.location), {
            state: _object_spread_props(_object_spread({}, getNonDuperHistoryState()), {
                duperbrowseRoot: true
            })
        }));
    }
    yield all([
        call(updateHistoryWith, currentItem),
        put(loadDuperbrowseItem(currentOpenItem, itemSchema, undefined))
    ]);
    typeof window !== 'undefined' && scrollTo(window, 0, 0);
}
function* handleExitDuperbrowse() {
    const currentItem = yield select(getCurrentItem);
    if (!currentItem) {
        // no current item, restore to original location
        const rootLocation = yield select(getRootLocation);
        if (rootLocation) {
            history.push(rootLocation);
        }
        yield put(exitDuperbrowseDone());
        return;
    }
    yield call(updateHistoryWith, currentItem);
}
// We removed the lastViewedItem after a timeout so
// we avoid any problems with scroll restoration if the component
// re-renders. This comes at cost of re-render :(
function* handleExitDuperbrowseDone() {
    yield delay(500);
    yield put(exitDuperbrowseResorationTimeout());
}
export default function* rootSaga() {
    yield all([
        takeEvery(moveNext, handleMoveNext),
        takeEvery(movePrev, handleMovePrev),
        takeEvery(duperbrowseHistoryChanged, handleHistoryChange),
        takeEvery(historyChanged, handleNonDuperHistoryChange),
        takeEvery(loadDuperbrowse, handleLoadDuperbrowse),
        takeEvery(openDuperbrowse, handleOpenDuperbrowse),
        takeEvery(exitDuperbrowse, handleExitDuperbrowse),
        takeLatest(loadDuperbrowseItem, handleLoading),
        takeLatest(exitDuperbrowseDone, handleExitDuperbrowseDone),
        takeLatest(shutdownDuperbrowse, handleExitDuperbrowseDone)
    ]);
}
